import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ElementRef,
  ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PaymentSelectionModalComponent } from './payment-selection-modal/payment-selection-modal.component';
import { PurchaseService } from '../../../../services/purchase.service';
import { PaymentMethodType, PurchaseFormService } from '../../../../services/purchase-form.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import { Language } from '../../../../shared/models/language';
import { Utility } from '../../../../services/utility/util';
import { CommonModule } from '@angular/common';
import { PricePipe } from "../../../../services/utility/price.pipe";

@Component({
  selector: 'app-purchase-footer',
  standalone: true,
  imports: [CommonModule, FooterComponent, PaymentSelectionModalComponent, TranslateModule, PricePipe],
  templateUrl: './purchase-footer.component.html',
  styleUrls: ['./purchase-footer.component.css'],
})
export class PurchaseFooterComponent implements OnInit, OnDestroy {
  @Input() locale: string = 'da-DK';

  @ViewChild('changePaymentBtn')
  changePaymentBtn?: ElementRef<HTMLButtonElement>;

  isLocationError: boolean = false;
  isPaymentModalOpen: boolean = false;
  currentMethod: PaymentMethodType | null = null;

  private langChangeSub?: Subscription;

  constructor(
    public formService: PurchaseFormService,
    private purchaseService: PurchaseService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.setPaymentMethod();

    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.setPaymentMethod();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  private setPaymentMethod(): void {
    const savedDetails = this.formService.getSavedPaymentMethod();
    this.currentMethod = savedDetails ?? this.getDefaultPaymentMethod();
    this.formService.setPaymentMethod(this.currentMethod);
  }

  get isPayButtonEnabled(): boolean {
    return this.formService.isFormValid();
  }

  get currentPaymentMethod(): string {
    const method = this.currentMethod;
    switch (method) {
      case PaymentMethodType.FRISBII_VIPPS:
        return 'MobilePay';
      case PaymentMethodType.ApplePay:
        return 'Apple Pay';
      default:
        return this.translateService.instant('web.purchasePaymentCard');
    }
  }

  private getDefaultPaymentMethod(): PaymentMethodType {
    const isDanishUser = this.translateService.getCurrentLang() === Language.Danish;
    if (isDanishUser) {
      return PaymentMethodType.FRISBII_VIPPS;
    }

    const isIOS = Utility.isUserIOS();
    if (isIOS) {
      return PaymentMethodType.ApplePay;
    }

    return PaymentMethodType.PaymentCard;
  }

  getPaymentMethodIcon(): string {
    const method = this.currentMethod;
    switch (method) {
      case PaymentMethodType.FRISBII_VIPPS:
        return 'assets/icons/MobilePayIconWhite.png';
      case PaymentMethodType.ApplePay:
        return 'assets/icons/ApplePayIcon.png';
      case PaymentMethodType.PaymentCard:
        return 'assets/icons/CreditCardIconWhite.png';
      default:
        return '';
    }
  }

  async onPay(): Promise<void> {
    if (!this.isPayButtonEnabled) return;

    await this.purchaseService.purchaseOrder();
  }

  onChangePayment(): void {
    this.isPaymentModalOpen = true;
  }

  onPaymentModalClosed(): void {
    this.isPaymentModalOpen = false;

    setTimeout(() => {
      this.changePaymentBtn?.nativeElement.focus();
    }, 0);
  }

  onPaymentMethodChanged(method: PaymentMethodType): void {
    this.currentMethod = method;
    this.formService.setPaymentMethod(method);
  }
}
